import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía para configurar los proveedores de IA en MiPueblo.
 * La plataforma soporta 5 proveedores; este doc explica cómo conseguir
 * credenciales, cuándo elegir uno u otro, y cómo asignarlos por tenant.
 */
export function DocsProveedoresIAPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Proveedores de IA
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Configuración de proveedores de IA
        </h1>

        <div className="callout">
          <strong>Tiempo total:</strong> 30-45 min para dejar al menos un
          proveedor cloud funcionando. Los locales (LM Studio / Ollama)
          requieren más infra pero NO mandan datos afuera.
        </div>

        {/* ---- Para qué se usan ---- */}
        <Section title="Para qué usa IA la plataforma">
          <p>Hay dos features que consumen LLMs, ambas opt-in por tenant:</p>
          <Ul
            items={[
              <>
                <strong>Borrador IA de PQRSDFs</strong> — cuando el
                funcionario abre el composer para responder, la IA pre-rellena
                un borrador formal (Ley 1755/2015) que el funcionario edita y
                aprueba. Genera 1 llamada por PQRSDF nuevo abierto en composer.
              </>,
              <>
                <strong>Bot RAG del chat ciudadano</strong> — responde
                consultas comunes con base en el Centro de Ayuda del tenant.
                Decide si responde o escala a operador. 1 llamada por turno de
                conversación bot.
              </>,
            ]}
          />
          <p>
            Ambas features están <strong>desactivadas por defecto</strong>{" "}
            en cada tenant. El super_admin las activa por tenant después de
            que la plataforma tenga al menos un proveedor configurado.
          </p>
        </Section>

        {/* ---- Tabla comparativa ---- */}
        <Section title="Tabla comparativa de proveedores">
          <Table
            head={["Proveedor", "Costo aprox", "Latencia", "Calidad", "Privacidad"]}
            rows={[
              [
                "Anthropic (Claude)",
                "$0.0003 / draft (Haiku)",
                "1-3s",
                "Alta — mejor en español formal",
                "Datos cifrados en tránsito, no entrenan con tu data",
              ],
              [
                "OpenAI (ChatGPT)",
                "$0.0002 / draft (4o-mini)",
                "1-2s",
                "Alta — muy versátil",
                "Igual que arriba si firmas DPA",
              ],
              [
                "Google Gemini",
                "$0.0001 / draft (Flash)",
                "1-2s",
                "Buena — algo más conciso",
                "Pueden usar datos según ToS gratuito",
              ],
              [
                "LM Studio (local)",
                "$0 marginal, CPU/GPU tuya",
                "5-30s según hardware",
                "Variable según modelo",
                "100% local, nada sale del server",
              ],
              [
                "Ollama (local)",
                "$0 marginal, CPU/GPU tuya",
                "5-30s",
                "Variable",
                "100% local",
              ],
            ]}
          />
          <div className="callout tip">
            💡 <strong>Recomendación por defecto:</strong> Anthropic Claude
            Haiku 4.5 para arrancar — mejor relación calidad/precio para
            español colombiano formal y latencia OK.
          </div>
        </Section>

        {/* ---- Anthropic ---- */}
        <Section title="Anthropic (Claude) — cómo obtener API key">
          <Ol>
            <li>
              Ir a <ExtLink href="https://console.anthropic.com">console.anthropic.com</ExtLink>
            </li>
            <li>
              Crear cuenta con email corporativo de MiPueblo (ej.{" "}
              <code>ops@mipuebloapp.com</code>).
            </li>
            <li>
              Configurar método de pago en <Kbd>Settings → Billing</Kbd>. Sin
              billing activo solo te dan créditos de prueba que se agotan.
            </li>
            <li>
              <Kbd>Settings → API Keys</Kbd> → <Kbd>Create Key</Kbd>. Nombre:{" "}
              <code>mipueblo-prod</code>. Workspace: Default.
            </li>
            <li>
              Copia la key (<code>sk-ant-api03-...</code>) — solo se muestra
              una vez.
            </li>
            <li>
              <strong>Aplicar Tier 2 o superior</strong> en{" "}
              <Kbd>Settings → Limits</Kbd>. El tier 1 tiene rate limits muy
              bajos (5 req/min) que cualquier alcaldía mediana satura.
            </li>
          </Ol>
          <p>Modelos disponibles (configurables en el panel por tenant):</p>
          <Table
            head={["ID", "Cuándo usarlo"]}
            rows={[
              [
                "claude-haiku-4-5-20251001",
                "Default. Económico y rápido. Para 99% de tenants.",
              ],
              [
                "claude-sonnet-4-6",
                "Cuando la alcaldía maneja PQRSDFs complejos (denuncias, situaciones jurídicas delicadas) y quiere borradores más elaborados. ~5x el costo.",
              ],
            ]}
          />
        </Section>

        {/* ---- OpenAI ---- */}
        <Section title="OpenAI — cómo obtener API key">
          <Ol>
            <li>
              <ExtLink href="https://platform.openai.com">platform.openai.com</ExtLink>{" "}
              → crear cuenta con email corporativo.
            </li>
            <li>
              <Kbd>Settings → Billing</Kbd> → agregar tarjeta y cargar al
              menos $5 de saldo.
            </li>
            <li>
              <Kbd>API keys</Kbd> → <Kbd>Create new secret key</Kbd>. Nombre:{" "}
              <code>mipueblo-prod</code>. Permissions:{" "}
              <code>Restricted → Model capabilities: write</code>.
            </li>
            <li>
              Copia la key (<code>sk-proj-...</code>).
            </li>
            <li>
              Pedir bajada de rate limits si vas a usar a escala — por
              defecto te ponen Tier 1.
            </li>
          </Ol>
          <Table
            head={["Modelo", "Cuándo usarlo"]}
            rows={[
              [
                "gpt-4o-mini",
                "Default OpenAI. Comparable a Haiku en costo y rapidez.",
              ],
              [
                "gpt-4o",
                "Para borradores más elaborados. 10-15x más caro que mini.",
              ],
              [
                "gpt-5",
                "Máxima calidad. Solo justificable para tenants enterprise.",
              ],
            ]}
          />
        </Section>

        {/* ---- Gemini ---- */}
        <Section title="Google Gemini — cómo obtener API key">
          <Ol>
            <li>
              <ExtLink href="https://aistudio.google.com">aistudio.google.com</ExtLink>{" "}
              → entrar con cuenta Google de MiPueblo.
            </li>
            <li>
              <Kbd>Get API key</Kbd> → <Kbd>Create API key in new project</Kbd>.
            </li>
            <li>Copia la key (<code>AIza...</code>).</li>
            <li>
              Para producción real, NO uses el tier gratuito (Google entrena
              con esos datos). Ve a{" "}
              <ExtLink href="https://console.cloud.google.com">console.cloud.google.com</ExtLink>{" "}
              → habilita billing → la misma key pasa a tier de pago.
            </li>
          </Ol>
          <Table
            head={["Modelo", "Cuándo usarlo"]}
            rows={[
              ["gemini-2.5-flash", "Default. Muy rápido y barato."],
              ["gemini-2.5-pro", "Calidad alta, más caro."],
            ]}
          />
        </Section>

        {/* ---- LM Studio ---- */}
        <Section title="LM Studio (local, self-hosted)">
          <p>
            Para alcaldías que no quieren que <strong>nada</strong> salga del
            datacenter (típico en municipios con políticas estrictas de
            datos). Requiere un server con GPU decente (mínimo 16GB VRAM
            para correr modelos útiles).
          </p>
          <Ol>
            <li>
              Descargar{" "}
              <ExtLink href="https://lmstudio.ai">lmstudio.ai</ExtLink> en el
              server (Linux/Mac/Win).
            </li>
            <li>
              Dentro de LM Studio → <Kbd>Discover</Kbd> → buscar y descargar
              un modelo. Sugeridos para español:
              <ul>
                <li>
                  <code>llama-3.3-70b-instruct</code> (necesita ~40GB VRAM)
                </li>
                <li>
                  <code>qwen2.5-32b-instruct</code> (16GB suficientes,
                  excelente en español)
                </li>
                <li>
                  <code>mistral-small-instruct</code> (rápido, 16GB)
                </li>
              </ul>
            </li>
            <li>
              <Kbd>Developer</Kbd> tab → <Kbd>Start server</Kbd>. Por
              defecto expone OpenAI-compat en{" "}
              <code>http://localhost:1234</code>.
            </li>
            <li>
              Exponer el endpoint a la red interna del VPS (NUNCA al
              internet abierto). Si MiPueblo y LM Studio corren en el mismo
              VPS, basta con el localhost.
            </li>
            <li>
              Probar:{" "}
              <pre className="code-block">{`curl http://localhost:1234/v1/models`}</pre>
              Debe devolver el JSON con el modelo cargado.
            </li>
          </Ol>
          <div className="callout warn">
            La calidad de los modelos locales para español formal es
            inferior a Claude/GPT. Probar bien antes de prometerle a un
            tenant que el borrador IA va a quedar bueno.
          </div>
        </Section>

        {/* ---- Ollama ---- */}
        <Section title="Ollama (local)">
          <p>
            Alternativa a LM Studio, headless, mejor para servers. CLI puro.
          </p>
          <Ol>
            <li>
              Instalar:{" "}
              <pre className="code-block">{`curl -fsSL https://ollama.com/install.sh | sh`}</pre>
            </li>
            <li>
              Bajar un modelo:{" "}
              <pre className="code-block">{`ollama pull qwen2.5:32b
ollama pull llama3.3:70b   # si tienes la VRAM`}</pre>
            </li>
            <li>
              El daemon expone OpenAI-compat en{" "}
              <code>http://localhost:11434</code> automáticamente.
            </li>
            <li>
              Verificar:{" "}
              <pre className="code-block">{`curl http://localhost:11434/v1/models`}</pre>
            </li>
          </Ol>
        </Section>

        {/* ---- Asignar ---- */}
        <Section title="Asignar proveedor a un tenant">
          <p>Tres lugares donde se configura IA:</p>
          <Table
            head={["Lugar", "Qué controla"]}
            rows={[
              [
                "/platform/config",
                "API keys y base URLs DE TODOS los proveedores a nivel plataforma. Cada tenant elige cuál usar, pero las credenciales viven aquí (cifradas con KEK).",
              ],
              [
                "/tenants/{id}/ai-draft",
                "Para ese tenant: enabled + provider + model que usa el borrador IA de PQRSDFs.",
              ],
              [
                "/tenants/{id}/soporte-rag",
                "Para ese tenant: enabled + provider + model que usa el bot del chat ciudadano. Puede ser distinto al del borrador.",
              ],
            ]}
          />
          <Ol>
            <li>
              Ir a <Kbd>/platform/config</Kbd> en el panel super-admin.
            </li>
            <li>
              Pegar al menos una API key (Anthropic recomendado para
              arrancar) → <Kbd>Guardar</Kbd>.
            </li>
            <li>
              Click <Kbd>Probar</Kbd> al lado de cada proveedor — debe
              decir &quot;OK&quot;. Si dice error, revisa la key.
            </li>
            <li>
              Ir al tenant → tarjeta <Kbd>Asistente IA</Kbd> → activar
              toggle, elegir proveedor + modelo → <Kbd>Guardar</Kbd>.
            </li>
            <li>
              Para el bot del chat: tarjeta <Kbd>Bot del chat</Kbd> →
              mismo flujo. <Kbd>Probar el bot</Kbd> al final con una
              consulta real para validar.
            </li>
          </Ol>
        </Section>

        {/* ---- Costos ---- */}
        <Section title="Costo aproximado por conversación">
          <p>
            Estimación basada en uso real medido (PQRSDF promedio ~400 tokens
            input, ~600 output; turno de chat bot ~800 input, ~200 output):
          </p>
          <Table
            head={["Proveedor / modelo", "Costo / borrador PQRSDF", "Costo / turno chat", "1000 PQRSDFs/mes"]}
            rows={[
              ["Anthropic Haiku 4.5", "~$0.0003", "~$0.0002", "~$0.30 USD"],
              ["Anthropic Sonnet 4.6", "~$0.005", "~$0.003", "~$5 USD"],
              ["OpenAI gpt-4o-mini", "~$0.0002", "~$0.0002", "~$0.20 USD"],
              ["OpenAI gpt-4o", "~$0.004", "~$0.003", "~$4 USD"],
              ["Gemini 2.5 Flash", "~$0.0001", "~$0.0001", "~$0.10 USD"],
              ["LM Studio / Ollama", "$0", "$0", "$0 (más costo eléctrico)"],
            ]}
          />
          <div className="callout tip">
            💡 Para una alcaldía mediana (500-2000 PQRSDFs/mes), el costo de
            IA es despreciable comparado con el del plan que paga. Lo que
            sube es si activas Sonnet/gpt-4o por gusto sin justificación.
          </div>
        </Section>

        {/* ---- Rotar keys ---- */}
        <Section title="Rotar API keys sin downtime">
          <p>
            Cuando se filtra una key o toca rotación semestral. El truco:
            generar la nueva ANTES de revocar la vieja.
          </p>
          <Ol>
            <li>
              En el dashboard del proveedor: crear nueva key paralela. La
              vieja sigue activa.
            </li>
            <li>
              <Kbd>/platform/config</Kbd> → pegar la nueva en lugar de la
              vieja → <Kbd>Guardar</Kbd>. El cambio aplica al siguiente
              request (no hay restart).
            </li>
            <li>
              Click <Kbd>Probar</Kbd> → confirmar OK.
            </li>
            <li>
              Esperar 5 min — si todo sigue funcionando, ir al dashboard
              del proveedor y revocar la vieja key.
            </li>
          </Ol>
          <div className="callout warn">
            Si una key se filtró públicamente (commit a git, screenshot
            público), <strong>revoca primero</strong> y después rota — el
            downtime de 30s es preferible al fraude.
          </div>
        </Section>

        {/* ---- Smoke test ---- */}
        <Section title="Smoke test manual del proveedor">
          <p>
            El endpoint{" "}
            <code>POST /super/platform/providers/{`{provider}`}/test</code>{" "}
            hace un ping real al proveedor con &quot;Responde solo
            &apos;OK&apos;&quot; y retorna ok/error + el texto recibido.
          </p>
          <p>
            Desde el panel: botón <Kbd>Probar</Kbd> al lado de cada
            proveedor en <Kbd>/platform/config</Kbd>.
          </p>
          <p>Desde curl, si necesitas debug:</p>
          <pre className="code-block">{`curl -X POST https://api.mipuebloapp.com/super/platform/providers/anthropic/test \\
  -H "Authorization: Bearer $SUPER_TOKEN" \\
  -H "Content-Type: application/json"`}</pre>
          <p>Respuestas típicas:</p>
          <Table
            head={["Error", "Causa", "Fix"]}
            rows={[
              [
                "Falta API key de Anthropic",
                "No has guardado la key en /platform/config",
                "Guárdala y reintenta",
              ],
              [
                "authentication_error: invalid x-api-key",
                "Key inválida o revocada",
                "Generar nueva en console.anthropic.com",
              ],
              [
                "rate_limit_error",
                "Tier muy bajo para tu carga",
                "Pedir bump de tier al proveedor",
              ],
              [
                "Connection refused (LM Studio/Ollama)",
                "El servidor local no está arriba o base_url mal",
                "Verificar curl base_url/v1/models",
              ],
              [
                "El proveedor devolvió un borrador vacío",
                "Modelo no soporta el formato pedido",
                "Cambiar a modelo más capable",
              ],
            ]}
          />
        </Section>

        {/* ---- Fallback ---- */}
        <Section title="Cuando un proveedor falla — fallback">
          <p>
            La función <code>generate_draft</code> levanta{" "}
            <code>AIDraftError</code> si el proveedor falla. El frontend del
            composer captura esto y muestra un toast con el detalle —{" "}
            <strong>el funcionario sigue pudiendo responder a mano</strong>{" "}
            usando las plantillas estáticas (acuse, trasladar, etc.).
          </p>
          <p>
            Para el bot RAG es similar: si el LLM falla,{" "}
            <code>answer_with_rag</code> devuelve <code>None</code> y el
            chat escala automáticamente a operador humano.
          </p>
          <p>
            Es decir: <strong>la plataforma nunca depende críticamente del
            proveedor IA</strong>. Si Anthropic está caído, los funcionarios
            redactan a mano como siempre lo hicieron, los chats escalan a
            operadores. No hay que entrar en pánico.
          </p>
          <div className="callout tip">
            💡 Si un proveedor sufre outage prolongado (raro pero pasa),
            puedes cambiar TODOS los tenants a otro proveedor con un
            UPDATE masivo en la DB sobre <code>tenants.settings</code>{" "}
            (campo JSONB). Pídelo en backlog si se vuelve recurrente —
            podemos tener un endpoint para esto.
          </div>
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {children} ↗
    </a>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
